import { ValidatorFn, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { environment } from 'src/environments/environment';

export class AppConstants {
  static readonly phonePrefix = '5';
  static readonly version = `?v=${environment.version}`;
  static readonly phoneMaxLength = 9;
  static readonly phoneMinLength = 9;
  static readonly phonePlaceholder = '5XXXXXXXX';

  static readonly emailPlaceholder = 'example@example.com';
  static readonly emailMaxLength = 50;
  static readonly emailMinLength = 7;
  static readonly emailPattern = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$';

  static readonly passwordPlaceholder = 'Password';
  static readonly passwordMaxLength = 20;
  static readonly passwordMinLength = 6;
  static readonly passwordPattern =
    '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{6,20}$';

  static readonly otpStartPlaceholder = '➤';

  static readonly phonePattern = '^5[0-9]{7}$';
  static phonePatternFun(): ValidatorFn {
    return Validators.pattern(/^5\d{8}$/);
  }

  static readonly otpMaxLength = 1;
  static readonly otpMinLength = 1;
  static readonly otpAllFields = Array(6);

  static readonly userNameMaxLength = 25;
  static readonly userNameMinLength = 3;

  static readonly fullNameMaxLength = 70;
  static readonly fullNameMinLength = 2;

  static readonly micEmail = '@mic-ksa.com';
  static readonly attachmentsWithOutMessage = '$$$-Attachment Without Message-$$$';
  static readonly mailPassword = '##$$Test@';
  static readonly mailFullName = 'Not appointed yet';
  static readonly fetchLimitApp = 10;
  static readonly mailSize = '100M';

  static readonly subjectMaxLength = 100;

  // Toolbar modules for rich text editor
  static readonly toolbarModules: Record<string, any> = {
    toolbar: {
      container: [
        [{ align: [] }, { direction: 'rtl' }],
        [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote'],
        ['clean'],
        ['attachment', 'image'],
      ],
      handlers: {
        attachment: function (this: any) {
          const quill = this.quill;
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.accept = '*/*';
          input.click();

          input.onchange = () => {
            const files = input.files;
            if (!files || files.length === 0) return;

            // تحويل FileList إلى مصفوفة
            const selectedFiles = Array.from(files);

            // 🟢 إضافة الملفات المختارة إلى المتغير العام
            if (!this.attachments) {
              this.attachments = [];
            }
            this.attachments.push(...selectedFiles);

            // إدراج معاينة الصور أو أسماء الملفات في المحرر
            selectedFiles.forEach((file) => {
              const range = quill.getSelection(true);

              if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                  quill.insertEmbed(range.index, 'image', e.target.result);
                  quill.setSelection(range.index + 1);
                };
                reader.readAsDataURL(file);
              } else {
                const icon = getFileIcon(file.name);
                quill.insertText(range.index, ` ${icon} ${file.name} `, {
                  bold: true,
                  color: '#0070C0',
                });
                quill.setSelection(range.index + file.name.length + 3);
              }
            });
          };

          function getFileIcon(fileName: string): string {
            const ext = fileName.split('.').pop()?.toLowerCase();
            switch (ext) {
              case 'pdf':
                return '📕';
              case 'doc':
              case 'docx':
                return '📄';
              case 'xls':
              case 'xlsx':
                return '📊';
              case 'ppt':
              case 'pptx':
                return '📈';
              case 'zip':
              case 'rar':
                return '🗜️';
              default:
                return '📎';
            }
          }
        },
      },
    },
  } as Record<string, any>;

  static isEmptyString(value?: string): boolean {
    return !value || value.trim().length === 0;
  }

  static formatInvitationHtml(input: string): string {
    if (!input) return '';

    let formatted = input
      .replace(/\\r\\n/g, '\n')
      .replace(/\\uD83D\\uDCE7/g, '📧')
      .replace(/\\uD83D\\uDCDE/g, '📞');

    return formatted
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .join('<br>'); // فقرتين
  }

  static formatInvitationHtmlMarkdown(input: string): SafeHtml {
    if (!input) return '';

    // let formatted = input.replace(/\\r\\n/g, '\n');
    let formatted = input
      .replace(/\\r\\n/g, '\n')
      .replace(/\n\s*-\s*\n/g, '\n- ')
      .replace(/\n{3,}/g, '\n\n');
    // Enable friendlier line breaks and GFM lists
    marked.setOptions({ gfm: true, breaks: true });
    const html = marked.parse(formatted);
    const x = html as SafeHtml;
    return x;
  }

  static markdownToPlainText(input: string): string {
    if (!input) return '';
    marked.setOptions({ gfm: true, breaks: true });
    const html = marked.parse(
      input
        .replace(/\\r\\n/g, '\n')
        .replace(/\n\s*-\s*\n/g, '\n- ')
        .replace(/\n{3,}/g, '\n\n'),
    );
    const text = String(html)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text;
  }

  static getFirstLetters(text: string): string {
    if (!text) return '';

    return text
      .split(' ')
      .filter((word) => word)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  static getFirstLettersWithCount(text: string, count: number = 2): string {
    if (!text) return '';
    return text.substring(0, count);
  }

  static removeMicKsaEmails(input: string): string {
    if (!input) return '';
    // Replace any local-part@mic-ksa.com with just the local-part
    let output = input.replace(/([\w.+-]+)@mic-ksa\.com/gi, '$1');
    // Remove angle brackets and collapse extra spaces
    output = output
      .replace(/[<>]/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return output;
  }

  static extractNames(inputs: string[]): string {
    const names = inputs.map((input) => {
      // حاول نجيب الاسم بين " "
      const match = input.match(/"([^"]+)"/);
      if (match) return match[1];

      // لو ما في اسم نرجع الإيميل
      const emailMatch = input.match(/<([^>]+)>/);
      return emailMatch ? emailMatch[1] : input;
    });

    // نجمعهم بفاصلة ومسافة
    return names.join(', ');
  }

  static extractEmails(inputs: string[]): string {
    const emails = inputs.map((input) => {
      // استخراج الإيميل من <email@domain.com>
      const emailMatch = input.match(/<([^>]+)>/);
      if (emailMatch) return emailMatch[1];

      // لو ما في <> نرجع النص كله
      return input;
    });

    // نجمعهم بفاصلة ومسافة
    return emails.join(', ');
  }

  static checkWordInText({
    currentText,
    checkWords,
    exactMatch = false,
  }: CheckWordOptions): string | null {
    if (!currentText) {
      return null;
    }

    const normalizedText = currentText.trim().toLowerCase();
    const words = Array.isArray(checkWords) ? checkWords : [checkWords];

    for (const word of words) {
      const normalizedWord = word.trim().toLowerCase();

      if (exactMatch) {
        // مطابقة كلمة كاملة فقط (مثلاً "error" مش "errors")
        const regex = new RegExp(`\\b${normalizedWord}\\b`, 'i');
        if (regex.test(normalizedText)) {
          return word; // رجع الكلمة اللي لقيتها
        }
      } else {
        // مجرد وجود الكلمة بداخل النص
        if (normalizedText.includes(normalizedWord)) {
          return word;
        }
      }
    }

    return null; // مافي أي كلمة مطابقة
  }

  static getUsernameFromEmail(email: string): string {
    return this.cutTextAtChar(email, '@');
  }

  static cutTextAtChar(text: string, stopChar: string): string {
    if (!text) return '';
    if (!stopChar) return text;

    const index = text.indexOf(stopChar);
    if (index !== -1) {
      return text.substring(0, index);
    }
    return text;
  }

  static checkStartsWithNumber(value: string): boolean {
    const regex = /^[0-9]/;
    return regex.test(value);
  }

  static checkIsNumericText(text: string): boolean {
    return /^[0-9]+$/.test(text);
  }

  static removeStartChar(text: string, startChar: string): string {
    if (!text) return '';
    if (!startChar) return text;

    if (text.startsWith(startChar)) {
      return text.substring(1); // تحذف أول حرف فقط
    }
    return text;
  }

  static checkStartsWithChar(text: string, char: string): boolean {
    if (!text || text.length === 0) return false;
    return text.charAt(0) === char;
  }

  static encodeDeviceDataToBase64(data: {
    phoneNumber: string;
    deviceId: string;
    userAgent: string;
    platform: string;
    language: string;
    vendor: string;
  }): string {
    const values = [
      data.phoneNumber,
      data.deviceId,
      data.userAgent,
      data.platform,
      data.language,
      data.vendor,
    ];

    const joinedText = values.join(',');

    const base64String = btoa(unescape(encodeURIComponent(joinedText)));
    const urlSafeBase64String = this.encodeToUrlSafeBase64(base64String);

    return urlSafeBase64String;
  }

  static encodeToUrlSafeBase64(input: string): string {
    const base64 = btoa(unescape(encodeURIComponent(input)));
    return base64
      .replace(/\+/g, '-') // استبدال +
      .replace(/\//g, '_') // استبدال /
      .replace(/=+$/, ''); // حذف =
  }

  static ensureStartsWithSlash(path: string): string {
    if (!path) return '';
    return path.startsWith('/') ? path : `/${path}`;
  }

  static formatFileSize(sizeInKbText: string, decimals: number = 2): string {
    const size = parseFloat(sizeInKbText.replace(' KB', '').replace(' MB', ''));

    if (isNaN(size) || size <= 0) return '0 KB';

    const sizeInMb = size / 1024;

    if (sizeInMb >= 1) {
      return `${sizeInMb.toFixed(decimals)} MB`;
    } else {
      return `${size.toFixed(decimals)} KB`;
    }
  }

  private static readonly imageTypes = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'bmp',
    'tiff',
    'svg',
    'heic',
  ]);
  private static readonly videoTypes = new Set([
    'mp4',
    'mov',
    'avi',
    'wmv',
    'flv',
    'mkv',
    'webm',
    'mpeg',
    '3gp',
    'ogg',
  ]);
  private static readonly audioTypes = new Set(['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma']);
  private static readonly documentTypes = new Set([
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'txt',
    'rtf',
    'odt',
    'csv',
    'md',
  ]);
  private static readonly archiveTypes = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz']);

  static getFileType(fileNameOrType: string): string {
    if (!fileNameOrType) return 'file';

    const ext = fileNameOrType.split('.').pop()?.toLowerCase() ?? '';
    if (!ext) return 'file';

    if (this.imageTypes.has(ext)) return 'image';
    if (this.videoTypes.has(ext)) return 'video';
    if (this.audioTypes.has(ext)) return 'audio';
    if (this.documentTypes.has(ext)) return 'document';
    if (this.archiveTypes.has(ext)) return 'archive';

    return 'file';
  }
}

interface CheckWordOptions {
  currentText: string;
  checkWords: string | string[];
  exactMatch?: boolean;
}
